import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../ui/ConfirmDialog';
import { keepAlive } from '../../services/authService';
import {
  KEEPALIVE_AFTER_MS,
  WARNING_LEAD_MS,
  broadcastSessionSignal,
  getLastActivity,
  getLastServerTouch,
  markActivity,
  markActivityNow,
  subscribeSessionSignal,
} from '../../services/sessionActivity';

// Kullanıcının "burada" olduğunu gösteren girdiler. Arka plan istekleri ve zamanlayıcılar
// SAYILMAZ (NIST: "abonenin etkinliği"); yalnız kişinin kendi hareketi.
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'wheel', 'scroll'];
const TICK_MS = 1000;

const isMediaPlaying = () => Array.from(document.querySelectorAll('video, audio'))
  .some((el) => !el.paused && !el.ended && el.readyState > 2);

// WEB HAREKETSİZLİK ZAMAN AŞIMI — istemci tarafı (2026-10-04).
//
// Süre kiracının Şirket Ayarları'ndan gelir (oturum yanıtındaki sessionIdleTimeoutMinutes). Süre
// dolmadan 2 dk önce geri sayımlı uyarı açılır (WCAG 2.2.1: uyarı + tek hareketle uzatma);
// dolunca oturum kapanır ve giriş ekranı nedeni söyler. Sunucu aynı pencereyi (+ pay) ayrıca
// uygular — bu bileşen yalnız DENEYİMDİR, güvenlik sınırı değildir.
//
// Sekmeler ortaktır: etkinlik ve sunucu dokunuşu localStorage'dan okunur; bir sekmede
// "sürdür" ya da çıkış diğerlerine duyurulur (sessionActivity).
export default function IdleSessionGuard({ timeoutMinutes, onIdleLogout, onLogout }) {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = useState(null);
  const warningOpenRef = useRef(false);
  const keepAliveInFlightRef = useRef(false);
  const loggedOutRef = useRef(false);
  const timeoutMs = Math.max(1, Number(timeoutMinutes) || 30) * 60 * 1000;

  const pingServer = useCallback(() => {
    if (keepAliveInFlightRef.current) return;
    keepAliveInFlightRef.current = true;
    // Yanıt (başarılı ya da değil) sunucu dokunuşunu api.js interceptor'ı işaretler; 401 ise
    // oturum zaten bitmiştir ve interceptor girişe yönlendirir.
    Promise.resolve(keepAlive())
      .catch(() => {})
      .finally(() => { keepAliveInFlightRef.current = false; });
  }, []);

  const endForIdle = useCallback(() => {
    if (loggedOutRef.current) return;
    loggedOutRef.current = true;
    warningOpenRef.current = false;
    setSecondsLeft(null);
    onIdleLogout();
  }, [onIdleLogout]);

  const tick = useCallback(() => {
    if (loggedOutRef.current) return;
    // Oynatılan video/ses (eğitim içeriği) kullanıcının ekranda olduğunu gösterir: 45 dakikalık
    // bir eğitim videosunu izleyen kişi girdi üretmez ama hareketsiz de değildir. (Gömülü,
    // başka kökenli oynatıcıların durumu okunamaz — orada uyarı açılır, tek tıkla sürdürülür.)
    if (!warningOpenRef.current && isMediaPlaying()) {
      markActivity();
      if (Date.now() - getLastServerTouch() >= KEEPALIVE_AFTER_MS) pingServer();
    }
    const remaining = getLastActivity() + timeoutMs - Date.now();
    if (remaining <= 0) {
      endForIdle();
      return;
    }
    if (remaining <= WARNING_LEAD_MS) {
      warningOpenRef.current = true;
      setSecondsLeft(Math.ceil(remaining / 1000));
    } else if (warningOpenRef.current) {
      // Başka bir sekme oturumu sürdürdü.
      warningOpenRef.current = false;
      setSecondsLeft(null);
    }
  }, [timeoutMs, endForIdle, pingServer]);

  // Giriş / sayfa yüklemesi = etkinlik.
  useEffect(() => {
    markActivityNow();
  }, []);

  useEffect(() => {
    const onActivity = () => {
      // Uyarı açıkken pasif hareket (fareyi kıpırdatmak) oturumu UZATMAZ — açık bir eylem ister.
      if (warningOpenRef.current || loggedOutRef.current) return;
      markActivity();
      if (Date.now() - getLastServerTouch() >= KEEPALIVE_AFTER_MS) pingServer();
    };
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true, capture: true }));
    return () => ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity, { capture: true }));
  }, [pingServer]);

  useEffect(() => {
    const id = setInterval(tick, TICK_MS);
    // Uyuyan dizüstü / arka plandaki sekme: zamanlayıcılar duraklar; görünür olunca hemen hesapla.
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', tick);
    };
  }, [tick]);

  useEffect(() => subscribeSessionSignal((type) => {
    if (type === 'idle-logout') {
      if (loggedOutRef.current) return;
      loggedOutRef.current = true;
      onIdleLogout({ broadcast: false });
    } else if (type === 'continue') {
      warningOpenRef.current = false;
      setSecondsLeft(null);
    }
  }), [onIdleLogout]);

  const handleContinue = useCallback(() => {
    markActivityNow();
    warningOpenRef.current = false;
    setSecondsLeft(null);
    pingServer();
    broadcastSessionSignal('continue');
  }, [pingServer]);

  const handleLogoutNow = useCallback(() => {
    loggedOutRef.current = true;
    warningOpenRef.current = false;
    setSecondsLeft(null);
    onLogout();
  }, [onLogout]);

  const minutes = Math.floor((secondsLeft || 0) / 60);
  const seconds = String((secondsLeft || 0) % 60).padStart(2, '0');

  return (
    <ConfirmDialog
      isOpen={secondsLeft !== null}
      type="info"
      title={t('session.idleWarningTitle')}
      message={(
        <>
          {t('session.idleWarningMessage', { minutes: Math.round(timeoutMs / 60000) })}
          <br />
          <strong data-testid="idle-countdown" aria-live="polite">
            {t('session.idleWarningCountdown', { time: `${minutes}:${seconds}` })}
          </strong>
        </>
      )}
      confirmText={t('session.continue')}
      cancelText={t('session.logoutNow')}
      onConfirm={handleContinue}
      onCancel={handleLogoutNow}
      onDismiss={handleContinue}
    />
  );
}
